import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, IconButton, TextField, InputAdornment, MenuItem, Chip, Button, Stack, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, CircularProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, ToggleButtonGroup, ToggleButton,
  Snackbar, Checkbox, FormControlLabel, Divider,
} from '@mui/material';
import {
  Refresh, Search, AddCircleOutline, AccountTree, SwapHoriz, DeleteOutline, OpenInNew,
  CheckCircle, RadioButtonUnchecked,
} from '@mui/icons-material';
import serialNumberService, { SERIAL_STATUSES, SerialRow, SerialStatus } from '../../services/serialNumberService';
import { productService, Product } from '../../services/productService';
import customerService, { Customer } from '../../services/customerService';
import { projectService, Project } from '../../services/projectService';
import { inventoryService, Warehouse } from '../../services/inventoryService';
import {
  PageHeader, StatCard, FilterBar, FilterSpacer, TableSurface, headerCellSx, bodyRowSx,
  StatusDot, StatusTone, ErrorState, TableEmptyState,
} from '../../components/common/pageKit';

// ---------------------------------------------------------------------------
// Status presentation
// ---------------------------------------------------------------------------

const STATUS_TONE: Record<SerialStatus, StatusTone> = {
  AVAILABLE: 'success', RESERVED: 'info', ISSUED: 'warning', INSTALLED: 'warning',
  RETURNED: 'neutral', UNDER_REPAIR: 'neutral', SCRAPPED: 'error',
};
const STATUS_TINT: Record<SerialStatus, string> = {
  AVAILABLE: '#059669', RESERVED: '#0284c7', ISSUED: '#d97706', INSTALLED: '#ca8a04',
  RETURNED: '#64748b', UNDER_REPAIR: '#7c3aed', SCRAPPED: '#dc2626',
};

const STAGE_TITLE: Record<string, string> = {
  RECEIPT: 'Receipt (GRN)', BATCH: 'Batch', WAREHOUSE: 'Warehouse',
  RESERVATION: 'Reservation', ISSUE_DELIVERY: 'Issue / Delivery', CUSTOMER_PROJECT: 'Customer / Project',
};

const fmtDate = (d?: string | null): string => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

// ---------------------------------------------------------------------------
// Debounced search-as-you-type hooks — small, page-local, not shared state.
// ---------------------------------------------------------------------------

function useProductSearch() {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!query.trim()) { setOptions([]); return; }
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      productService.searchProducts(query.trim())
        .then((res) => { if (alive) setOptions(res.products || []); })
        .catch(() => { if (alive) setOptions([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [query]);
  return { query, setQuery, options, loading };
}

function useCustomerSearch() {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!query.trim()) { setOptions([]); return; }
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      customerService.searchCustomers(query.trim())
        .then((res) => { if (alive) setOptions(res || []); })
        .catch(() => { if (alive) setOptions([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [query]);
  return { query, setQuery, options, loading };
}

function useProjectSearch() {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!query.trim()) { setOptions([]); return; }
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      projectService.getProjects({ search: query.trim() })
        .then((res) => { if (alive) setOptions(res || []); })
        .catch(() => { if (alive) setOptions([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [query]);
  return { query, setQuery, options, loading };
}

// ---------------------------------------------------------------------------
// Genealogy dialog
// ---------------------------------------------------------------------------

interface GenealogyStage { stage: string; present: boolean; label: string; link?: string | null }

const GenealogyDialog: React.FC<{ serial: SerialRow; onClose: () => void }> = ({ serial, onClose }) => {
  const [stages, setStages] = useState<GenealogyStage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    serialNumberService.genealogy(serial.id)
      .then((res) => setStages(res.stages || []))
      .catch((e: any) => setError(e?.message || 'Failed to load genealogy'));
  }, [serial.id]);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Genealogy — {serial.serial_number}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
          {serial.product_name} ({serial.product_code})
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error ? (
          <ErrorState message={error} />
        ) : stages === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={0} sx={{ mt: 0.5 }}>
            {stages.map((s, i) => (
              <Box key={s.stage} sx={{ display: 'flex', gap: 1.25, pb: i < stages.length - 1 ? 2 : 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {s.present ? <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} /> : <RadioButtonUnchecked sx={{ fontSize: 20, color: 'text.disabled' }} />}
                  {i < stages.length - 1 && <Box sx={{ width: 2, flex: 1, minHeight: 18, bgcolor: 'divider', mt: 0.25 }} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, pb: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{STAGE_TITLE[s.stage] || s.stage}</Typography>
                    {s.link && (
                      <Tooltip title="Open">
                        <IconButton size="small" onClick={() => window.open(s.link as string, '_blank', 'noopener')}>
                          <OpenInNew sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="caption" color={s.present ? 'text.secondary' : 'text.disabled'}>{s.label}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Register serial(s) dialog
// ---------------------------------------------------------------------------

const RegisterDialog: React.FC<{ onClose: () => void; onDone: (message: string) => void }> = ({ onClose, onDone }) => {
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const productSearch = useProductSearch();
  const [product, setProduct] = useState<Product | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState<number | ''>('');
  const [serialNumber, setSerialNumber] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [warrantyStart, setWarrantyStart] = useState('');
  const [warrantyEnd, setWarrantyEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    inventoryService.getWarehouses().then((res) => setWarehouses(res.warehouses || [])).catch(() => {});
  }, []);

  const parsedSerials = useMemo(
    () => Array.from(new Set(bulkText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean))),
    [bulkText]
  );

  const submit = async () => {
    if (!product) { setErr('Select a product'); return; }
    setBusy(true);
    setErr(null);
    try {
      if (mode === 'single') {
        if (!serialNumber.trim()) { setErr('Enter a serial number'); setBusy(false); return; }
        await serialNumberService.create({
          productId: Number(product.id),
          serialNumber: serialNumber.trim(),
          warehouseId: warehouseId || undefined,
          warrantyStart: warrantyStart || undefined,
          warrantyEnd: warrantyEnd || undefined,
          notes: notes || undefined,
        });
        onDone('Serial number registered');
      } else {
        if (parsedSerials.length === 0) { setErr('Paste at least one serial number'); setBusy(false); return; }
        const res = await serialNumberService.bulkCreate({
          productId: Number(product.id),
          warehouseId: warehouseId || undefined,
          serialNumbers: parsedSerials,
        });
        onDone(`${res.createdCount} registered, ${res.skippedCount} skipped as duplicates`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Failed to register serial number(s)');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Register Serial Number(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
            <ToggleButton value="single" sx={{ textTransform: 'none', flex: 1 }}>Single</ToggleButton>
            <ToggleButton value="bulk" sx={{ textTransform: 'none', flex: 1 }}>Bulk</ToggleButton>
          </ToggleButtonGroup>

          <Autocomplete
            size="small"
            options={productSearch.options}
            loading={productSearch.loading}
            filterOptions={(x) => x}
            getOptionLabel={(o: Product) => `${o.product_code} — ${o.product_name}`}
            isOptionEqualToValue={(o: Product, v: Product) => o.id === v.id}
            value={product}
            onChange={(_, v) => setProduct(v)}
            onInputChange={(_, v) => productSearch.setQuery(v)}
            renderInput={(params) => <TextField {...params} label="Product" placeholder="Search product code or name" size="small" fullWidth />}
          />

          <TextField
            select size="small" label="Warehouse (optional)" value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value === '' ? '' : Number(e.target.value))} fullWidth
          >
            <MenuItem value="">Unassigned</MenuItem>
            {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.warehouseName} ({w.warehouseCode})</MenuItem>)}
          </TextField>

          {mode === 'single' ? (
            <>
              <TextField label="Serial Number" size="small" fullWidth value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
              <Stack direction="row" spacing={1.5}>
                <TextField label="Warranty Start" type="date" size="small" fullWidth value={warrantyStart}
                  onChange={(e) => setWarrantyStart(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="Warranty End" type="date" size="small" fullWidth value={warrantyEnd}
                  onChange={(e) => setWarrantyEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Stack>
              <TextField label="Notes" size="small" fullWidth multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </>
          ) : (
            <>
              <TextField
                label="Serial Numbers" size="small" fullWidth multiline minRows={5} value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={'One per line, or comma-separated\ne.g.\nSN-1001\nSN-1002\nSN-1003'}
              />
              <Typography variant="caption" color="text.secondary">{parsedSerials.length} unique serial number{parsedSerials.length === 1 ? '' : 's'} parsed</Typography>
            </>
          )}

          {err && <Typography variant="caption" color="error">{err}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy} startIcon={busy ? <CircularProgress size={15} /> : <AddCircleOutline />}>
          Register
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Status change dialog
// ---------------------------------------------------------------------------

const StatusChangeDialog: React.FC<{ serial: SerialRow; onClose: () => void; onDone: (message: string) => void }> = ({ serial, onClose, onDone }) => {
  const [status, setStatus] = useState<SerialStatus>(serial.status);
  const [force, setForce] = useState(false);
  const customerSearch = useCustomerSearch();
  const projectSearch = useProjectSearch();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await serialNumberService.setStatus(serial.id, status, {
        customerId: customer?.id, projectId: project?.id, force: force || undefined,
      });
      onDone(`Serial ${serial.serial_number} moved to ${status}`);
    } catch (e: any) {
      setErr(e?.message || 'Status change failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Change Status — {serial.serial_number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" color="text.secondary">Current:</Typography>
            <StatusDot label={serial.status} tone={STATUS_TONE[serial.status]} />
          </Stack>
          <TextField select size="small" label="New Status" value={status} onChange={(e) => setStatus(e.target.value as SerialStatus)} fullWidth>
            {SERIAL_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          {(status === 'ISSUED' || status === 'INSTALLED' || status === 'RETURNED') && (
            <>
              <Autocomplete
                size="small"
                options={customerSearch.options}
                loading={customerSearch.loading}
                filterOptions={(x) => x}
                getOptionLabel={(o: Customer) => o.company_name}
                isOptionEqualToValue={(o: Customer, v: Customer) => o.id === v.id}
                value={customer}
                onChange={(_, v) => setCustomer(v)}
                onInputChange={(_, v) => customerSearch.setQuery(v)}
                renderInput={(params) => <TextField {...params} label="Customer (optional)" size="small" fullWidth />}
              />
              <Autocomplete
                size="small"
                options={projectSearch.options}
                loading={projectSearch.loading}
                filterOptions={(x) => x}
                getOptionLabel={(o: Project) => o.project_name}
                isOptionEqualToValue={(o: Project, v: Project) => o.id === v.id}
                value={project}
                onChange={(_, v) => setProject(v)}
                onInputChange={(_, v) => projectSearch.setQuery(v)}
                renderInput={(params) => <TextField {...params} label="Project (optional)" size="small" fullWidth />}
              />
            </>
          )}
          <FormControlLabel
            control={<Checkbox size="small" checked={force} onChange={(e) => setForce(e.target.checked)} />}
            label={<Typography variant="caption">Force override (allow an unusual transition)</Typography>}
          />
          {err && <Typography variant="caption" color="error">{err}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || status === serial.status} startIcon={busy ? <CircularProgress size={15} /> : <SwapHoriz />}>
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Delete confirmation dialog
// ---------------------------------------------------------------------------

const DeleteConfirmDialog: React.FC<{ serial: SerialRow; onClose: () => void; onDone: (message: string) => void }> = ({ serial, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await serialNumberService.remove(serial.id);
      onDone(`Serial ${serial.serial_number} deleted`);
    } catch (e: any) {
      setErr(e?.message || 'Delete failed');
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Delete Serial Number</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Delete serial <b>{serial.serial_number}</b> ({serial.product_name})? This cannot be undone.
        </Typography>
        {err && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{err}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="error" onClick={submit} disabled={busy} startIcon={busy ? <CircularProgress size={15} /> : <DeleteOutline />}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const SerialNumbersPage: React.FC = () => {
  const [stats, setStats] = useState<{ byStatus: Record<string, number>; total: number; warrantyExpiringSoonCount: number } | null>(null);
  const [serials, setSerials] = useState<SerialRow[]>([]);
  const [pagination, setPagination] = useState<{ total: number; page: number; limit: number; totalPages: number } | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<SerialStatus | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [genealogyFor, setGenealogyFor] = useState<SerialRow | null>(null);
  const [statusChangeFor, setStatusChangeFor] = useState<SerialRow | null>(null);
  const [deleteFor, setDeleteFor] = useState<SerialRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(0); }, [statusFilter, search]);

  const loadStats = useCallback(() => {
    serialNumberService.stats().then(setStats).catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    setListLoading(true);
    setError(null);
    serialNumberService
      .list({ status: statusFilter || undefined, search: search || undefined, page: page + 1, limit: rowsPerPage })
      .then((res) => { setSerials(res.serials || []); setPagination(res.pagination || null); })
      .catch((e: any) => setError(e?.message || 'Failed to load serial numbers'))
      .finally(() => setListLoading(false));
  }, [statusFilter, search, page, rowsPerPage]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadList(); }, [loadList]);

  const refreshAll = () => { loadStats(); loadList(); };

  const afterAction = (message: string) => {
    setToast(message);
    setRegisterOpen(false);
    setStatusChangeFor(null);
    setDeleteFor(null);
    refreshAll();
  };

  const inUseCount = (stats?.byStatus?.ISSUED || 0) + (stats?.byStatus?.INSTALLED || 0);

  return (
    <Box>
      <PageHeader
        title="Serial Numbers"
        subtitle="Unit-level traceability for serial-tracked products — status ledger and full genealogy"
        actions={
          <>
            <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => setRegisterOpen(true)}>
              Register Serial(s)
            </Button>
            <IconButton onClick={refreshAll} disabled={listLoading}>
              <Refresh />
            </IconButton>
          </>
        }
      />

      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Total Serials" value={stats ? stats.total : null} icon={<AccountTree />} color="#0284c7" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Available" value={stats ? stats.byStatus.AVAILABLE || 0 : null} icon={<CheckCircle />} color={STATUS_TINT.AVAILABLE} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="In Use (Issued / Installed)" value={stats ? inUseCount : null} icon={<SwapHoriz />} color={STATUS_TINT.ISSUED} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Warranty Expiring ≤30d" value={stats ? stats.warrantyExpiringSoonCount : null} icon={<RadioButtonUnchecked />} color="#dc2626" />
        </Grid>
      </Grid>

      <FilterBar>
        <TextField
          size="small" placeholder="Search serial number…" value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} color="disabled" /></InputAdornment> }}
        />
        <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as SerialStatus | '')} sx={{ minWidth: 170 }}>
          <MenuItem value="">All statuses</MenuItem>
          {SERIAL_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <Stack direction="row" spacing={0.75} flexWrap="wrap">
          {SERIAL_STATUSES.map((s) => (
            <Chip
              key={s} size="small" label={`${s}${stats ? ` (${stats.byStatus[s] || 0})` : ''}`}
              clickable variant={statusFilter === s ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
              sx={{ borderColor: 'divider', ...(statusFilter === s ? { bgcolor: `${STATUS_TINT[s]}1f`, color: STATUS_TINT[s] } : {}) }}
            />
          ))}
        </Stack>
        <FilterSpacer />
        <Typography variant="body2" color="text.secondary">
          <b>{pagination?.total ?? serials.length}</b> serial{(pagination?.total ?? serials.length) === 1 ? '' : 's'}
        </Typography>
      </FilterBar>

      {error ? (
        <ErrorState message={error} onRetry={loadList} />
      ) : (
        <TableSurface>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Serial Number', 'Product', 'Warehouse', 'Batch', 'Status', 'Customer / Project', 'Warranty End', 'Actions'].map((h) => (
                    <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {listLoading && serials.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ border: 0, py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : serials.length === 0 ? (
                  <TableRow><TableCell colSpan={8} sx={{ border: 0 }}>
                    <TableEmptyState
                      title={statusFilter || search ? 'No serials match these filters' : 'No serial numbers registered yet'}
                      hint={statusFilter || search ? 'Try a different search or status.' : 'Register a serial-tracked product to get started.'}
                    />
                  </TableCell></TableRow>
                ) : serials.map((s) => (
                  <TableRow key={s.id} hover sx={bodyRowSx}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{s.serial_number}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{s.product_name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.product_code}</Typography>
                    </TableCell>
                    <TableCell>{s.warehouse_name || '—'}</TableCell>
                    <TableCell>{s.batch_number || '—'}</TableCell>
                    <TableCell><StatusDot label={s.status} tone={STATUS_TONE[s.status]} /></TableCell>
                    <TableCell>{[s.customer_name, s.project_name].filter(Boolean).join(' / ') || '—'}</TableCell>
                    <TableCell>{fmtDate(s.warranty_end)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.25}>
                        <Tooltip title="Genealogy">
                          <IconButton size="small" onClick={() => setGenealogyFor(s)}><AccountTree sx={{ fontSize: 17 }} /></IconButton>
                        </Tooltip>
                        <Tooltip title="Change status">
                          <IconButton size="small" onClick={() => setStatusChangeFor(s)}><SwapHoriz sx={{ fontSize: 17 }} /></IconButton>
                        </Tooltip>
                        <Tooltip title={s.status === 'AVAILABLE' ? 'Delete' : 'Only AVAILABLE serials can be deleted'}>
                          <span>
                            <IconButton size="small" disabled={s.status !== 'AVAILABLE'} onClick={() => setDeleteFor(s)}>
                              <DeleteOutline sx={{ fontSize: 17 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(pagination?.total || 0) > 0 && (
            <TablePagination
              component="div"
              count={pagination?.total || 0}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </TableSurface>
      )}

      {registerOpen && <RegisterDialog onClose={() => setRegisterOpen(false)} onDone={afterAction} />}
      {genealogyFor && <GenealogyDialog serial={genealogyFor} onClose={() => setGenealogyFor(null)} />}
      {statusChangeFor && <StatusChangeDialog serial={statusChangeFor} onClose={() => setStatusChangeFor(null)} onDone={afterAction} />}
      {deleteFor && <DeleteConfirmDialog serial={deleteFor} onClose={() => setDeleteFor(null)} onDone={afterAction} />}

      <Snackbar open={!!toast} autoHideDuration={3200} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default SerialNumbersPage;
